// src/pages/EventRegistrationPage.js
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { 
  FaHome, 
  FaChevronRight, 
  FaCalendarAlt, 
  FaMapMarkerAlt, 
  FaClock, 
  FaUser,
  FaEnvelope,
  FaPhone,
  FaIdCard,
  FaBuilding,
  FaGraduationCap,
  FaInfoCircle,
  FaArrowLeft,
  FaCheckCircle,
  FaUsers
} from 'react-icons/fa';
import Section from '../components/common/Section';
import Heading from '../components/common/Heading';
import Button from '../components/common/Button';
import FormField from '../components/common/FormField';
import Card from '../components/common/Card';
import { getEventById } from '../data/eventsdata';

const EventRegistrationPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  
  // State for event data and form
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    organization: '',
    role: '',
    educationLevel: '',
    howDidYouHear: '',
    dietaryRestrictions: '',
    specialRequirements: '',
    agreeToTerms: false
  });
  
  // Form errors
  const [formErrors, setFormErrors] = useState({});
  
  // Fetch event data from eventsdata.js
  useEffect(() => {
    setLoading(true);
    
    // Simulate API delay
    setTimeout(() => {
      const foundEvent = getEventById(eventId);
      
      if (foundEvent) {
        // Ensure the event has proper registration structure
        const eventWithRegistration = {
          ...foundEvent,
          registration: foundEvent.registration || {
            required: true,
            deadline: new Date(new Date().getTime() + 7 * 24 * 60 * 60 * 1000).toISOString(), // Default to 7 days from now
            fee: "Free",
            availableSeats: 30,
            registrationType: "public",
            questions: [
              {
                id: "dietaryRestrictions",
                label: "Do you have any dietary restrictions?",
                type: "text",
                required: false
              },
              {
                id: "specialRequirements",
                label: "Do you have any special requirements for accessibility?",
                type: "text",
                required: false
              }
            ]
          }
        };
        
        setEvent(eventWithRegistration);
        setLoading(false);
      } else {
        setError('Event not found');
        setLoading(false);
      }
    }, 500);
  }, [eventId]);
  
  // Format date for display
  const formatDate = (dateString) => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };
  
  // Format time for display
  const formatTime = (dateString) => {
    const options = { hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleTimeString('en-US', options);
  };
  
  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    
    // Clear error when field is edited
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: null
      });
    }
  };
  
  // Validate form
  const validateForm = () => {
    const errors = {};
    
    // Required fields
    if (!formData.firstName.trim()) errors.firstName = 'First name is required';
    if (!formData.lastName.trim()) errors.lastName = 'Last name is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    else if (!/^\S+@\S+\.\S+$/.test(formData.email)) errors.email = 'Invalid email format';
    if (!formData.phone.trim()) errors.phone = 'Phone number is required';
    
    // Event-specific required fields
    if (event?.registration?.questions) {
      event.registration.questions.forEach(question => {
        if (question.required && !formData[question.id]) {
          errors[question.id] = `${question.label} is required`;
        }
      });
    }
    
    // Check terms agreement
    if (!formData.agreeToTerms) errors.agreeToTerms = 'You must agree to the terms and conditions';
    
    return errors;
  };
  
  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate form
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setFormErrors(validationErrors);
      // Scroll to the first error
      const firstErrorId = Object.keys(validationErrors)[0];
      const element = document.getElementsByName(firstErrorId)[0];
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus();
      }
      return;
    }
    
    // Submit form
    setIsSubmitting(true);
    
    // In a real app, this would be an API call
    setTimeout(() => {
      // Simulate successful registration
      setIsSubmitting(false);
      setRegistrationComplete(true);
      
      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 1500);
  };
  
  // Loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <Section background="white" padding="lg" className="pt-32">
        <div className="text-center">
          <Heading level={2}>Event Not Found</Heading>
          <p className="text-gray-600 mb-6">We couldn't find the event you're looking for.</p>
          <Button to="/events" variant="primary">View All Events</Button>
        </div>
      </Section>
    );
  }
  
  // Registration success state
  if (registrationComplete) {
    return (
      <>
        <Helmet>
          <title>Registration Confirmed - {event.title}</title>
        </Helmet>
        
        <Section background="white" padding="lg" className="pt-32">
          <Card className="max-w-3xl mx-auto">
            <div className="text-center">
              <div className="inline-flex items-center justify-center h-24 w-24 rounded-full bg-green-100 text-green-600 text-5xl mb-6">
                <FaCheckCircle />
              </div>
              
              <Heading level={2} className="mb-4">Registration Confirmed!</Heading>
              
              <p className="text-lg text-gray-600 mb-6">
                Thank you for registering for "{event.title}". We have sent a confirmation email to {formData.email} with all the details.
              </p>
              
              <div className="bg-gray-50 p-6 rounded-lg mb-8 text-left">
                <h3 className="font-bold text-primary-dark mb-4">Event Details</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div className="flex items-start">
                    <FaCalendarAlt className="text-secondary mt-1 mr-3 flex-shrink-0" />
                    <div>
                      <div className="font-medium">Date</div>
                      <div>{formatDate(event.date)}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <FaClock className="text-secondary mt-1 mr-3 flex-shrink-0" />
                    <div>
                      <div className="font-medium">Time</div>
                      <div>{formatTime(event.date)} - {formatTime(event.endDate || event.date)}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-start md:col-span-2">
                    <FaMapMarkerAlt className="text-secondary mt-1 mr-3 flex-shrink-0" />
                    <div>
                      <div className="font-medium">Location</div>
                      <div>{event.location}</div>
                      <div>{event.address}</div>
                    </div>
                  </div>
                </div>
                
                {event.registration.registrationType === 'paid' && (
                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-md mb-4">
                    <h4 className="font-bold text-yellow-800 flex items-center">
                      <FaInfoCircle className="mr-2" /> Payment Required
                    </h4>
                    <p className="text-yellow-800">
                      Please complete your payment to secure your spot. Check your email for payment instructions.
                    </p>
                  </div>
                )}
                
                <div className="text-gray-600">
                  <p className="mb-3">
                    A confirmation email has been sent with a QR code that you should present at check-in.
                  </p>
                  <p>
                    If you have any questions or need to update your registration details, please contact us at{' '}
                    <a href="mailto:events@leotechsl.com" className="text-primary hover:underline">
                      events@leotechsl.com
                    </a>
                  </p>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Button 
                  variant="primary" 
                  to={`/events/${eventId}`}
                  icon={<FaArrowLeft />}
                  iconPosition="left"
                >
                  Back to Event
                </Button>
                
                <Button 
                  variant="secondary" 
                  to="/events"
                >
                  Browse Other Events
                </Button>
              </div>
            </div>
          </Card>
        </Section>
      </>
    );
  }
  
  return (
    <>
      <Helmet>
        <title>Register for {event.title} - Leotech Events</title>
        <meta name="description" content={`Register to attend ${event.title}. ${event.description}`} />
      </Helmet>
      
      {/* Registration Header */}
      <section 
        className="relative pt-32 pb-12 bg-gradient-to-r from-primary-dark to-primary"
        style={event.image ? {
          backgroundImage: `linear-gradient(to right, rgba(13, 71, 161, 0.9), rgba(13, 94, 244, 0.9)), url(${event.image})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundBlendMode: 'overlay'
        } : {}}
      >
        <div className="container mx-auto px-4 relative z-10">
          {/* Breadcrumbs */}
          <div className="flex items-center justify-center mb-8 text-white text-sm">
            <Link to="/" className="flex items-center hover:text-secondary transition-colors">
              <FaHome className="mr-2" />
              Home
            </Link>
            <FaChevronRight className="mx-2 text-xs text-gray-300" />
            <Link to="/events" className="hover:text-secondary transition-colors">Events</Link>
            <FaChevronRight className="mx-2 text-xs text-gray-300" />
            <Link to={`/events/${eventId}`} className="hover:text-secondary transition-colors">Event Details</Link>
            <FaChevronRight className="mx-2 text-xs text-gray-300" />
            <span className="text-secondary font-medium">Registration</span>
          </div>
          
          <div className="text-center">
            <motion.h1 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-3xl md:text-4xl font-bold text-white mb-4"
            >
              Register for {event.title}
            </motion.h1>
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-white text-opacity-90 flex flex-wrap justify-center gap-4 mb-2"
            >
              <div className="flex items-center">
                <FaCalendarAlt className="mr-2 text-secondary" />
                <span>{formatDate(event.date)}</span>
              </div>
              <div className="flex items-center">
                <FaClock className="mr-2 text-secondary" />
                <span>{formatTime(event.date)} - {formatTime(event.endDate || new Date(new Date(event.date).getTime() + 2 * 60 * 60 * 1000).toISOString())}</span>
              </div>
              <div className="flex items-center">
                <FaMapMarkerAlt className="mr-2 text-secondary" />
                <span>{event.location}</span>
              </div>
            </motion.div>
            
            {event.registration.deadline && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="mt-4 inline-block bg-secondary text-white text-sm font-medium rounded-full px-4 py-2"
              >
                Registration Deadline: {formatDate(event.registration.deadline)}
              </motion.div>
            )}
          </div>
        </div>
      </section>
      
      {/* Registration Form */}
      <Section background="white" padding="lg">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column: Registration Form */}
          <div className="lg:col-span-2">
            <Card className="mb-8">
              <Heading level={3} className="mb-6">Personal Information</Heading>
              
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <FormField
                    label="First Name"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    error={formErrors.firstName}
                    required
                    placeholder="Enter your first name"
                    icon={<FaUser className="text-gray-400" />}
                  />
                  
                  <FormField
                    label="Last Name"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    error={formErrors.lastName}
                    required
                    placeholder="Enter your last name"
                    icon={<FaUser className="text-gray-400" />}
                  />
                  
                  <FormField
                    label="Email Address"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    error={formErrors.email}
                    required
                    placeholder="Enter your email address"
                    helpText="Registration confirmation will be sent to this email"
                    icon={<FaEnvelope className="text-gray-400" />}
                  />
                  
                  <FormField
                    label="Phone Number"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    error={formErrors.phone}
                    required
                    placeholder="Enter your phone number"
                    icon={<FaPhone className="text-gray-400" />}
                  />
                  
                  <FormField
                    label="Organization/Institution"
                    name="organization"
                    value={formData.organization}
                    onChange={handleInputChange}
                    error={formErrors.organization}
                    placeholder="Enter your organization or institution"
                    icon={<FaBuilding className="text-gray-400" />}
                  />
                  
                  <FormField
                    label="Role/Position"
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    error={formErrors.role}
                    placeholder="Enter your role or position"
                    icon={<FaIdCard className="text-gray-400" />}
                  />
                </div>
                
                {event.registration.questions && event.registration.questions.length > 0 && (
                  <>
                    <Heading level={4} className="mb-4">Additional Information</Heading>
                    
                    <div className="space-y-6 mb-8">
                      {event.registration.questions.map(question => {
                        if (question.type === 'text') {
                          return (
                            <FormField
                              key={question.id}
                              label={question.label}
                              name={question.id}
                              value={formData[question.id] || ''}
                              onChange={handleInputChange}
                              error={formErrors[question.id]}
                              required={question.required}
                            />
                          );
                        } else if (question.type === 'textarea') {
                          return (
                            <FormField
                              key={question.id}
                              label={question.label}
                              name={question.id}
                              type="textarea"
                              value={formData[question.id] || ''}
                              onChange={handleInputChange}
                              error={formErrors[question.id]}
                              required={question.required}
                            />
                          );
                        } else if (question.type === 'select') {
                          return (
                            <div key={question.id} className="mb-4">
                              <label
                                htmlFor={question.id}
                                className={`block mb-2 text-sm font-medium ${
                                  formErrors[question.id] ? 'text-red-600' : 'text-gray-800'
                                }`}
                              >
                                {question.label}
                                {question.required && <span className="text-red-500 ml-1">*</span>}
                              </label>
                              
                              <select
                                id={question.id}
                                name={question.id}
                                value={formData[question.id] || ''}
                                onChange={handleInputChange}
                                className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors ${
                                  formErrors[question.id]
                                    ? 'border-red-500 bg-red-50'
                                    : 'border-gray-300 focus:border-primary'
                                }`}
                                required={question.required}
                              >
                                <option value="">Select an option</option>
                                {question.options.map((option, index) => (
                                  <option key={index} value={option}>{option}</option>
                                ))}
                              </select>
                              
                              {formErrors[question.id] && (
                                <p className="mt-1 text-sm text-red-600">{formErrors[question.id]}</p>
                              )}
                            </div>
                          );
                        } else if (question.type === 'radio') {
                          return (
                            <div key={question.id} className="mb-4">
                              <label
                                className={`block mb-2 text-sm font-medium ${
                                  formErrors[question.id] ? 'text-red-600' : 'text-gray-800'
                                }`}
                              >
                                {question.label}
                                {question.required && <span className="text-red-500 ml-1">*</span>}
                              </label>
                              
                              {question.options.map((option, index) => (
                                <div key={index} className="flex items-center mb-2">
                                  <input
                                    id={`${question.id}-${index}`}
                                    type="radio"
                                    name={question.id}
                                    value={option}
                                    checked={formData[question.id] === option}
                                    onChange={handleInputChange}
                                    className="w-4 h-4 text-primary border-gray-300 focus:ring-primary"
                                    required={question.required && index === 0}
                                  />
                                  <label
                                    htmlFor={`${question.id}-${index}`}
                                    className="ml-2 text-sm text-gray-700"
                                  >
                                    {option}
                                  </label>
                                </div>
                              ))}
                              
                              {formErrors[question.id] && (
                                <p className="mt-1 text-sm text-red-600">{formErrors[question.id]}</p>
                              )}
                            </div>
                          );
                        }
                        
                        return null;
                      })}
                    </div>
                  </>
                )}
                
                {/* Payment Information (if applicable) */}
                {event.registration.registrationType === 'paid' && (
                  <>
                    <Heading level={4} className="mb-4">Payment Information</Heading>
                    
                    <div className="bg-gray-50 p-6 rounded-lg mb-8">
                      <div className="mb-4">
                        <div className="font-medium text-gray-700 mb-2">Registration Fee:</div>
                        <div className="text-xl font-bold text-primary-dark">{event.registration.fee}</div>
                      </div>
                      
                      {event.registration.paymentOptions && (
                        <div className="mb-4">
                          <div className="font-medium text-gray-700 mb-2">Payment Options:</div>
                          <div className="space-y-2">
                            {event.registration.paymentOptions.map((option, index) => (
                              <div key={index} className="flex items-center">
                                <input
                                  id={`payment-option-${index}`}
                                  type="radio"
                                  name="paymentMethod"
                                  value={option}
                                  checked={formData.paymentMethod === option}
                                  onChange={handleInputChange}
                                  className="w-4 h-4 text-primary border-gray-300 focus:ring-primary"
                                />
                                <label
                                  htmlFor={`payment-option-${index}`}
                                  className="ml-2 text-gray-700"
                                >
                                  {option}
                                </label>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {event.registration.paymentInstructions && (
                        <div className="text-gray-700 text-sm">
                          <p>{event.registration.paymentInstructions}</p>
                        </div>
                      )}
                    </div>
                  </>
                )}
                
                {/* Terms and Conditions */}
                <div className="mb-8">
                  <div className="flex items-center">
                    <input
                      id="agree-terms"
                      type="checkbox"
                      name="agreeToTerms"
                      checked={formData.agreeToTerms}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                    />
                    <label
                      htmlFor="agree-terms"
                      className={`ml-2 text-sm ${formErrors.agreeToTerms ? 'text-red-600' : 'text-gray-700'}`}
                    >
                      I agree to the <a href="/terms" target="_blank" className="text-primary hover:underline">terms and conditions</a> and <a href="/privacy-policy" target="_blank" className="text-primary hover:underline">privacy policy</a>.
                    </label>
                  </div>
                  {formErrors.agreeToTerms && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.agreeToTerms}</p>
                  )}
                </div>
                
                {/* Submit Buttons */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button 
                    type="submit" 
                    variant="secondary" 
                    size="lg"
                    disabled={isSubmitting}
                    className="flex-1"
                  >
                    {isSubmitting ? 'Processing...' : 'Complete Registration'}
                  </Button>
                  
                  <Button 
                    type="button"
                    variant="outline-primary" 
                    size="lg"
                    onClick={() => navigate(`/events/${eventId}`)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </Card>
          </div>
          
          {/* Right Column: Event Details */}
          <div className="lg:col-span-1">
            <div className="sticky top-32">
              <Card className="mb-6" shadow="lg">
                <div className="relative h-40 overflow-hidden rounded-t-lg -mx-6 -mt-6 mb-6">
                  <img
                    src={event.image}
                    alt={event.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = `https://placehold.co/800x400/1a237e/ffffff?text=${event.title.replace(/ /g, '+')}`;
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent opacity-60"></div>
                  <div className="absolute bottom-0 left-0 p-4">
                    <div className="text-white font-bold text-xl">{event.title}</div>
                  </div>
                </div>
                
                <Heading level={4} className="mb-4">Event Details</Heading>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-start">
                    <FaCalendarAlt className="text-secondary mt-1 mr-3 flex-shrink-0" />
                    <div>
                      <div className="font-medium">Date</div>
                      <div className="text-gray-600">{formatDate(event.date)}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <FaClock className="text-secondary mt-1 mr-3 flex-shrink-0" />
                    <div>
                      <div className="font-medium">Time</div>
                      <div className="text-gray-600">
                        {formatTime(event.date)} - {formatTime(event.endDate || new Date(new Date(event.date).getTime() + 2 * 60 * 60 * 1000).toISOString())}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <FaMapMarkerAlt className="text-secondary mt-1 mr-3 flex-shrink-0" />
                    <div>
                      <div className="font-medium">Location</div>
                      <div className="text-gray-600">{event.location}</div>
                      {event.address && <div className="text-gray-600">{event.address}</div>}
                    </div>
                  </div>
                  
                  {event.registration.availableSeats && (
                    <div className="flex items-start">
                      <FaUsers className="text-secondary mt-1 mr-3 flex-shrink-0" />
                      <div>
                        <div className="font-medium">Available Seats</div>
                        <div className="text-gray-600">{event.registration.availableSeats} remaining</div>
                      </div>
                    </div>
                  )}
                  
                  {event.registration.registrationType === 'paid' && (
                    <div className="flex items-start">
                      <FaInfoCircle className="text-secondary mt-1 mr-3 flex-shrink-0" />
                      <div>
                        <div className="font-medium">Registration Fee</div>
                        <div className="text-gray-600">{event.registration.fee}</div>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Event details from eventsdata */}
                {event.theme && (
                  <div className="mb-4">
                    <div className="font-medium">Theme:</div>
                    <div className="text-gray-600 italic">"{event.theme}"</div>
                  </div>
                )}
                
                {/* Only show speakers if available */}
                {event.speakers && event.speakers.length > 0 && (
                  <div className="mb-4">
                    <div className="font-medium mb-2">Featured Speakers:</div>
                    <ul className="space-y-2">
                      {event.speakers.map((speaker, index) => (
                        <li key={index} className="text-gray-600">
                          <span className="font-medium">{speaker.name}</span>
                          {speaker.title && <span>, {speaker.title}</span>}
                          {speaker.linkedin && (
                            <a 
                              href={speaker.linkedin} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-primary hover:underline ml-1 text-sm"
                            >
                              (LinkedIn)
                            </a>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Show partners if available */}
                {event.partners && event.partners.length > 0 && (
                  <div className="mb-4">
                    <div className="font-medium mb-2">In Partnership With:</div>
                    <ul className="space-y-1">
                      {event.partners.map((partner, index) => (
                        <li key={index} className="text-gray-600">• {partner}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Show schedule if available */}
                {event.schedule && event.schedule.length > 0 && (
                  <div className="mb-4">
                    <div className="font-medium mb-2">Event Schedule:</div>
                    <div className="space-y-3 text-sm">
                      {event.schedule.map((day, index) => (
                        <div key={index}>
                          <div className="font-bold">{day.day}: {day.title}</div>
                          <ul className="pl-5 space-y-1">
                            {day.activities.map((activity, idx) => (
                              <li key={idx} className="text-gray-600 list-disc">
                                {activity}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="border-t border-gray-200 pt-4">
                  <h4 className="font-medium text-gray-800 mb-2">Important Notes:</h4>
                  <ul className="text-sm text-gray-600 space-y-2">
                    <li>• Registration confirmation will be sent to your email</li>
                    <li>• Please arrive 15 minutes before the event starts</li>
                    {event.registration.registrationType === 'paid' && (
                      <li className="font-medium text-primary-dark">• Payment must be completed to secure your spot</li>
                    )}
                    <li>• For any questions, contact events@leotechsl.com</li>
                  </ul>
                </div>
              </Card>
              
              <Card shadow="md">
                <Heading level={4} className="mb-4">Need Help?</Heading>
                <p className="text-gray-600 mb-4">
                  If you encounter any issues with registration or have questions about the event, please don't hesitate to contact us:
                </p>
                <div className="space-y-2 text-gray-700">
                  <div className="flex items-center">
                    <FaEnvelope className="mr-2 text-primary" />
                    <a href="mailto:events@leotechsl.com" className="hover:text-primary">events@leotechsl.com</a>
                  </div>
                  <div className="flex items-center">
                    <FaPhone className="mr-2 text-primary" />
                    <span>+232 79727380</span>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </Section>
    </>
  );
};

export default EventRegistrationPage;